import { ListFilterBar } from '../../app/data-view.js';
import {
  Button,
  SidePanel,
  StatusPill,
  formatCurrency,
  useSnackbar,
  IconButton,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, Ban } from 'lucide-react';
import { useState } from 'react';
import { SectionCardHeader } from '../../app/section-card-header.js';
import {
  getApiError,
  schedulingApi,
  type AppointmentNotice,
  type AppointmentNoticeKind,
} from '../../lib/api.js';
import { formatDate, formatDateTime, formatHourMinute } from '../../lib/time-zone.js';

const NOTICES_KEY = ['company', 'scheduling', 'notices'] as const;

const kindLabel: Record<AppointmentNoticeKind, string> = {
  PORTAL_UPDATED: 'Atualização do portal',
  REQUESTED: 'Pedido recebido',
  TIME_OCCUPIED: 'Horário ocupado',
  DEPOSIT_EXPIRED: 'Prazo do sinal vencido',
};

/// O que o aviso leva além da mensagem principal, para a recepção saber antes de abrir.
function details(notice: AppointmentNotice): string[] {
  const zone = notice.timeZone;
  return [
    notice.queuePosition ? `Posição ${String(notice.queuePosition)} na fila` : null,
    notice.depositAmountCents > 0
      ? `Sinal de ${formatCurrency(notice.depositAmountCents, notice.currency)}${
          notice.depositDueAt
            ? ` até ${formatDate(notice.depositDueAt, zone)} às ${formatHourMinute(notice.depositDueAt, zone)}`
            : ''
        }`
      : null,
    notice.creditCents > 0
      ? `Crédito de ${formatCurrency(notice.creditCents, notice.currency)}`
      : null,
  ].filter((item): item is string => Boolean(item));
}

function emailText(notice: AppointmentNotice): string {
  return notice.emailState === 'QUEUED'
    ? 'E-mail na fila'
    : notice.emailState === 'SKIPPED'
      ? 'Sem e-mail'
      : 'E-mail em preparo';
}

/// Preparar abre o WhatsApp de quem clicou com o aviso pronto (ADR 0036). A janela abre no clique:
/// aberta só depois da resposta da API, o navegador a trataria como pop-up.
function useNoticeActions() {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [pending, setPending] = useState<string | null>(null);

  async function run(notice: AppointmentNotice, action: 'prepare' | 'dismiss') {
    const tab = action === 'prepare' ? window.open('', '_blank') : null;
    setPending(notice.id);
    try {
      if (action === 'dismiss') {
        await schedulingApi.dismissNotice(notice.id);
        snackbar.success('Aviso dispensado.');
      } else {
        const result = await schedulingApi.prepareNotice(notice.id);
        if (tab && result.whatsappUrl) {
          tab.opener = null;
          tab.location.href = result.whatsappUrl;
          snackbar.success('WhatsApp aberto com o aviso pronto. Confira e envie.');
        } else {
          tab?.close();
          snackbar.failure(
            result.whatsappUrl
              ? 'O navegador bloqueou a janela do WhatsApp. Libere pop-ups deste endereço e tente de novo.'
              : 'Cliente sem celular cadastrado: avise por outro meio e dispense o aviso.',
          );
        }
      }
      await queryClient.invalidateQueries({ queryKey: NOTICES_KEY });
    } catch (cause) {
      tab?.close();
      snackbar.failure(getApiError(cause));
    } finally {
      setPending(null);
    }
  }

  return { run, pending };
}

function NoticeItem({
  notice,
  canManage,
  showAppointment,
  actions,
}: {
  notice: AppointmentNotice;
  canManage: boolean;
  showAppointment: boolean;
  actions: ReturnType<typeof useNoticeActions>;
}) {
  const busy = actions.pending === notice.id;
  return (
    <li className="rounded-xl border border-surface-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold">{kindLabel[notice.kind]}</p>
        {notice.state === 'PENDING' ? (
          <StatusPill tone="warning">A preparar</StatusPill>
        ) : notice.state === 'PREPARED' ? (
          <StatusPill tone="positive">WhatsApp preparado</StatusPill>
        ) : (
          <StatusPill tone="neutral">Sem efeito</StatusPill>
        )}
      </div>
      {showAppointment ? (
        <>
          <p className="mt-1 text-sm">
            {notice.clientDisplayName} · {notice.serviceDisplayName}
          </p>
          <p className="mt-1 text-xs text-content-muted">
            {formatDateTime(notice.startsAt, notice.timeZone)} · {notice.professionalDisplayName}
          </p>
        </>
      ) : null}
      {notice.portalText ? <p className="mt-3 text-sm leading-6">{notice.portalText}</p> : null}
      {details(notice).length ? (
        <p className="mt-1 text-xs text-content-muted">{details(notice).join(' · ')}</p>
      ) : null}
      <p className="mt-1 text-xs text-content-muted">
        {emailText(notice)}
        {notice.preparedByName ? ` · preparado por ${notice.preparedByName}` : ''}
      </p>
      {canManage && notice.state !== 'OBSOLETE' ? (
        <div className="mt-3 flex flex-wrap gap-1">
          <IconButton
            label={`${notice.state === 'PREPARED' ? 'Abrir de novo' : 'Abrir WhatsApp'}: ${kindLabel[notice.kind]} de ${notice.clientDisplayName}`}
            icon={<MessageCircle />}
            loading={busy}
            onClick={() => void actions.run(notice, 'prepare')}
          />
          {notice.state === 'PENDING' ? (
            <IconButton
              label={`Dispensar: ${kindLabel[notice.kind]} de ${notice.clientDisplayName}`}
              icon={<Ban />}
              disabled={busy}
              onClick={() => void actions.run(notice, 'dismiss')}
            />
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/// Os avisos da fila que ainda precisam de alguém (ADR 0058): o botão some quando não há nenhum.
export function AppointmentNoticesButton({ canManage }: { canManage: boolean }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const actions = useNoticeActions();
  const query = useQuery({
    queryKey: [...NOTICES_KEY, 'PENDING'],
    queryFn: () => schedulingApi.listNotices({ state: 'PENDING' }),
    refetchInterval: 60_000,
  });
  const notices = query.data ?? [];
  if (!notices.length && !open) return null;
  const normalize = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const visible = notices.filter((notice) =>
    normalize(
      `${notice.clientDisplayName} ${notice.serviceDisplayName} ${kindLabel[notice.kind]}`,
    ).includes(normalize(search)),
  );
  return (
    <>
      <Button
        variant="outline"
        onClick={() => {
          setSearch('');
          setOpen(true);
        }}
      >
        <MessageCircle className="size-4" /> Avisos a enviar ({notices.length})
      </Button>
      <SidePanel
        open={open}
        onOpenChange={setOpen}
        title="Avisos a enviar"
        description="Mensagens da fila prontas para o WhatsApp de quem clicar. O sistema não envia: abrir marca o aviso como preparado. O e-mail, quando o cliente tem, vai sozinho."
      >
        <ListFilterBar
          variant="panel"
          searchLabel="Buscar aviso"
          search={search}
          onSearchChange={setSearch}
          count={`${visible.length} ${visible.length === 1 ? 'aviso' : 'avisos'}`}
        />
        <ol className="mt-4 space-y-3" aria-label="Avisos a enviar">
          {visible.map((notice) => (
            <NoticeItem
              key={notice.id}
              notice={notice}
              canManage={canManage}
              showAppointment
              actions={actions}
            />
          ))}
        </ol>
        {visible.length === 0 ? (
          <p className="mt-4 text-sm">Nenhum aviso pendente encontrado.</p>
        ) : null}
      </SidePanel>
    </>
  );
}

/// Os avisos deste pedido, na página do atendimento.
export function AppointmentNoticesCard({
  appointmentId,
  canManage,
}: {
  appointmentId: string;
  canManage: boolean;
}) {
  const actions = useNoticeActions();
  const query = useQuery({
    queryKey: [...NOTICES_KEY, appointmentId],
    queryFn: () => schedulingApi.listNotices({ appointmentId }),
  });
  const notices = query.data ?? [];
  if (!notices.length) return null;
  return (
    <div className="rounded-2xl border border-surface-border bg-surface-card shadow-soft">
      <SectionCardHeader title="Avisos ao cliente" icon={MessageCircle} />
      <ol className="grid gap-3 p-4" aria-label="Avisos ao cliente">
        {[...notices].reverse().map((notice) => (
          <NoticeItem
            key={notice.id}
            notice={notice}
            canManage={canManage}
            showAppointment={false}
            actions={actions}
          />
        ))}
      </ol>
    </div>
  );
}
